<template>
  <div class="profile-access">
    <page-header
      :title="$t('profileAccess.title')"
      boxed-xs
      heading="h3"
      underlined
      class="_header"
    />
    <m-content
      boxed-xs
      padding
    >
      <m-section
        v-if="showPassword"
        heading-size="h4"
        :title="$t('profileAccess.password.sectionHeading')"
        class="_section"
      >
        <settings-action-row>
          <template #title>
            {{ $t('profileAccess.password.heading') }}
          </template>
          <template #description>
            {{ $t('profileAccess.password.subHeading') }}
          </template>
          <m-btn @click="showPasswordPrompt">
            {{ $t('profileAccess.password.button') }}
          </m-btn>
        </settings-action-row>
      </m-section>
      <m-section
        v-if="showPersonalAccessTokens"
        heading-size="h4"
        :title="$t('personalAccessTokens.title')"
        :sub-title="$t('personalAccessTokens.sectionDescription', { days: maxLifetimeDays })"
        class="_section"
      >
        <m-card
          v-if="createdToken !== ''"
          bordered
          flat
          padding-small
          class="_token-details"
        >
          <div class="_title">
            {{ $t('personalAccessTokens.createdTitle', { name: createdTokenName }) }}
          </div>
          <div class="_hint">
            {{ $t('personalAccessTokens.createdHint') }}
          </div>
          <div class="_secret">
            <m-input-password
              :value="createdToken"
              read-only
              class="_field"
            />
            <m-btn
              icon="copy"
              fab
              small
              hide-border
              @click="copyToken"
            />
            <m-btn
              icon="close"
              fab
              small
              hide-border
              light
              @click="createdToken = ''"
            />
          </div>
        </m-card>
        <m-table
          :columns="columns"
          :data-source="rows"
          row-key="uid"
          hide-vertical-border
          show-new-button
          :new-button-title="$t('personalAccessTokens.create')"
          class="_table"
          @new="openCreateDialog"
        >
          <template #expiresAt="{ row }">
            <div class="_expires _date">
              {{ formatDate(row.expiresAt) }}
              <m-tag
                v-if="isExpired(row)"
                small
                :color="$colors.red.base"
                :title="$t('personalAccessTokens.expired')"
              />
            </div>
          </template>
          <template #rotatedAt="{ row }">
            <div class="_date">
              {{ formatDate(row.rotatedAt) }}
            </div>
          </template>
          <template #lastUsedAt="{ row }">
            <div class="_date">
              {{ row.lastUsedAt === null ? $t('personalAccessTokens.neverUsed') : formatDate(row.lastUsedAt) }}
            </div>
          </template>
          <template #actions="{ row }">
            <div class="_actions">
              <m-dropdown
                :title="$t('general.actions')"
                close-on-click
                @click.stop
              >
                <m-btn
                  icon="ellipsis"
                  fab
                  hide-border
                  small
                />
                <template #overlay>
                  <m-card
                    no-padding
                    list
                  >
                    <m-card-item
                      icon="retweet"
                      hide-border
                      @click="openRotateDialog(row)"
                    >
                      {{ $t('personalAccessTokens.rotate') }}
                    </m-card-item>
                    <m-card-item
                      icon="delete"
                      hide-border
                      @click="revoke(row)"
                    >
                      {{ $t('personalAccessTokens.revoke') }}
                    </m-card-item>
                  </m-card>
                </template>
              </m-dropdown>
            </div>
          </template>
        </m-table>
      </m-section>
    </m-content>

    <m-dialog
      v-model:value="showCreateDialog"
      :title="$t('personalAccessTokens.createTitle')"
      hide-footer
    >
      <m-form-item :label="$t('personalAccessTokens.name')">
        <m-text-field
          v-model:value="name"
          auto-focus
          :placeholder="$t('personalAccessTokens.namePlaceholder')"
          @keydown.enter="create"
        />
      </m-form-item>
      <m-form-item :label="$t('personalAccessTokens.expiresAtLabel')">
        <m-date-picker
          :value="expiresAt"
          :date-time="DateTime"
          :min="minExpiresAtDate"
          :max="maxExpiresAtDate"
          full-width
          @input="expiresAt = $event"
        />
        <div class="_hint">
          {{ $t('personalAccessTokens.expiresAtHint', { days: maxLifetimeDays }) }}
        </div>
      </m-form-item>
      <div class="_dialog-actions">
        <m-btn @click="showCreateDialog = false">
          {{ $t('general.cancel') }}
        </m-btn>
        <m-btn
          color="primary"
          :disabled="!canSubmitCreate"
          :loading="createLoading"
          @click="create"
        >
          {{ $t('personalAccessTokens.create') }}
        </m-btn>
      </div>
    </m-dialog>

    <m-dialog
      v-model:value="showRotateDialog"
      :title="$t('personalAccessTokens.rotateTitle')"
      hide-footer
    >
      <p class="_hint">
        {{ $t('personalAccessTokens.rotateHint', { name: tokenToRotate?.name }) }}
      </p>
      <m-form-item :label="$t('personalAccessTokens.expiresAtLabel')">
        <m-date-picker
          :value="expiresAt"
          :date-time="DateTime"
          :min="minExpiresAtDate"
          :max="maxExpiresAtDate"
          full-width
          @input="expiresAt = $event"
        />
        <div class="_hint">
          {{ $t('personalAccessTokens.expiresAtHint', { days: maxLifetimeDays }) }}
        </div>
      </m-form-item>
      <div class="_dialog-actions">
        <m-btn @click="showRotateDialog = false">
          {{ $t('general.cancel') }}
        </m-btn>
        <m-btn
          color="primary"
          :disabled="expiresAt === null"
          :loading="rotateLoading"
          @click="rotate"
        >
          {{ $t('personalAccessTokens.rotate') }}
        </m-btn>
      </div>
    </m-dialog>
  </div>
</template>

<script setup>
import PageHeader from 'shared/components/PageHeader.vue';
import SettingsActionRow from '@/components/SettingsActionRow.vue';
import useAccess from '@/composables/access/access';
import useConfirmDialog from '@/composables/confirm-dialog';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import usePersonalAccessTokens from '@/composables/personal-access-token/personal-access-token';
import useSnackBar from '@/composables/snackbar';
import { DEFAULT_LIFETIME_DAYS } from '@/lib/personal-access-token';
import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import { doSendPasswordForgetMail } from '@/api/sentinel';
import { email } from 'shared/composables/user';
import { experimentFlag } from 'shared/experiments.json';
import { featureFlag } from 'shared/constants.json';
import { logCatch } from '@/lib/logger/logger';
import { modalSizes } from 'shared/modal-sizes';
import { toISO } from 'shared/lib/time';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const snackbar = useSnackBar();
const { confirm } = useConfirmDialog();
const { accountHasFeature } = useAccess();
const { loggedInUser } = useLoggedInUser();
const {
  personalAccessTokens,
  maxLifetimeDays,
  createPersonalAccessToken,
  rotatePersonalAccessToken,
  revokePersonalAccessToken,
} = usePersonalAccessTokens();

const showPassword = computed(() => !accountHasFeature([featureFlag.restrictPasswordLogin]));
const showPersonalAccessTokens = computed(() => accountHasFeature([experimentFlag.papiMcp]));

const sendPasswordMail = () => doSendPasswordForgetMail({ email: email(loggedInUser.value) })
  .then((response) => {
    if (response.status !== 200) {
      snackbar.error();
      return;
    }
    snackbar.success(t('profileAccess.password.success'));
  });

const showPasswordPrompt = () => {
  confirm({
    title: t('profileAccess.password.promptTitle'),
    maxWidth: modalSizes.md,
    message: { type: 'info', text: t('profileAccess.password.content', { email: email(loggedInUser.value) }) },
    okText: t('profileAccess.password.okText'),
    okType: 'primary',
    onOk: sendPasswordMail,
  });
};

const rows = computed(() => [...personalAccessTokens.value].sort((a, b) => b.rotatedAt.localeCompare(a.rotatedAt)));

const columns = computed(() => [
  { key: 'name', dataIndex: 'name', title: t('personalAccessTokens.name') },
  { key: 'rotatedAt', title: t('personalAccessTokens.rotatedAt'), scopedSlots: { customRender: 'rotatedAt' }, width: 110 },
  { key: 'lastUsedAt', title: t('personalAccessTokens.lastUsedAt'), scopedSlots: { customRender: 'lastUsedAt' }, width: 110 },
  { key: 'expiresAt', title: t('personalAccessTokens.expiresAt'), scopedSlots: { customRender: 'expiresAt' }, width: 140 },
  { key: 'actions', dataIndex: 'actions', title: '', width: '30', scopedSlots: { customRender: 'actions' } },
]);

const formatDate = (value) => DateTime.fromISO(value).toLocaleString(DateTime.DATE_MED);
const isExpired = (token) => DateTime.fromISO(token.expiresAt) <= DateTime.local();

const latestDateWithin = (days) => DateTime.local().plus({ days: days - 1 }).toISODate();
const toExpiresAt = (value) => DateTime.fromISO(toISO(value), { setZone: true }).endOf('day').toISO();

const minExpiresAtDate = computed(() => DateTime.local().plus({ days: 1 }).toISODate());
const maxExpiresAtDate = computed(() => latestDateWithin(maxLifetimeDays.value));
const defaultExpiresAt = () => ({ startDate: latestDateWithin(Math.min(DEFAULT_LIFETIME_DAYS, maxLifetimeDays.value)) });

const expiresAt = ref(null);
const createdToken = ref('');
const createdTokenName = ref('');
const showSecret = (tokenName) => (secret) => {
  createdToken.value = secret;
  createdTokenName.value = tokenName;
  showCreateDialog.value = false;
  showRotateDialog.value = false;
};

const showCreateDialog = ref(false);
const name = ref('');
const createLoading = ref(false);
const canSubmitCreate = computed(() => name.value.trim() !== '' && expiresAt.value !== null);
const openCreateDialog = () => {
  name.value = '';
  expiresAt.value = defaultExpiresAt();
  showCreateDialog.value = true;
};
const create = () => {
  if (!canSubmitCreate.value || createLoading.value) {
    return;
  }
  createLoading.value = true;
  createPersonalAccessToken({ name: name.value.trim(), expiresAt: toExpiresAt(expiresAt.value) })
    .then(showSecret(name.value.trim()))
    .catch(logCatch(() => snackbar.error()))
    .finally(() => {
      createLoading.value = false;
    });
};

const showRotateDialog = ref(false);
const tokenToRotate = ref(null);
const rotateLoading = ref(false);
const openRotateDialog = (token) => {
  tokenToRotate.value = token;
  expiresAt.value = defaultExpiresAt();
  showRotateDialog.value = true;
};
const rotate = () => {
  if (expiresAt.value === null || rotateLoading.value) {
    return;
  }
  rotateLoading.value = true;
  rotatePersonalAccessToken(tokenToRotate.value, { expiresAt: toExpiresAt(expiresAt.value) })
    .then(showSecret(tokenToRotate.value.name))
    .catch(logCatch(() => snackbar.error()))
    .finally(() => {
      rotateLoading.value = false;
    });
};

const copyToken = () => {
  navigator.clipboard.writeText(createdToken.value);
  snackbar.success(t('success.copied'));
};

const revoke = (token) => {
  confirm({
    title: t('personalAccessTokens.revokePrompt', { name: token.name }),
    okText: t('personalAccessTokens.revoke'),
    onOk() {
      revokePersonalAccessToken(token).catch(logCatch(() => snackbar.error()));
    },
  });
};
</script>

<style scoped lang="scss" type="text/scss">
  .profile-access {
    ._section {
      margin-bottom: 4rem;
    }

    ._token-details {
      margin-top: 1.2rem;
      margin-bottom: 1.2rem;

      ._title {
        margin-bottom: .4rem;
        font-weight: $font-weight-medium;
      }

      ._secret {
        display: flex;
        gap: .4rem;
        align-items: center;

        ._field {
          flex: 1 1 auto;
        }
      }
    }

    ._date {
      white-space: nowrap;
    }

    ._expires {
      display: flex;
      gap: .6rem;
      align-items: center;
    }

    ._actions {
      display: flex;
      justify-content: flex-end;
    }
  }

  ._hint {
    margin-top: .4rem;
    margin-bottom: 1.2rem;
    font-size: $font-size-2;
    color: $font-color-secondary;
  }

  ._dialog-actions {
    display: flex;
    gap: .8rem;
    justify-content: flex-end;
    margin-top: 1.6rem;
  }
</style>
